import { HttpResponse } from '../http-response';
import { plainToClass } from 'class-transformer';
import { HttpErrorResponse } from '../http-error-response';
import { HttpRequest, RequestType, ResponseType } from '../http-request';
import localStorageService from '../../../../services/local-storage';
import axios, { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import { ApiCommunicationManager } from '../api-communication-manager.interface';

export class ApiCommunicationManagerImpl implements ApiCommunicationManager {
  private axios: AxiosInstance;

  constructor(baseUrl: string) {
    this.axios = axios.create({ baseURL: baseUrl });
  }

  async exec<T, K>(
    networkRequest: HttpRequest<T>,
    Model: any,
    options: {
      noAccess?: boolean;
      addLanguage?: boolean;
    } = {}
  ): Promise<HttpResponse<K> | HttpErrorResponse<K>> {
    await this.setAuthorization(networkRequest, options.noAccess);
    await this.setLanguage(networkRequest, options.addLanguage);

    return this.axios
      .request<HttpResponse<K>>({
        url: networkRequest.url,
        method: networkRequest.method,
        headers: networkRequest.headers,
        params: networkRequest.params || null,
        data: networkRequest.data || null,
        responseType: networkRequest.responseType || ResponseType.JSON,
      })
      .then((response: AxiosResponse) => {
        return ApiCommunicationManagerImpl.transformAxiosResponseToHttpResponse<K>(
          response,
          Model
        );
      })
      .catch(async (err: any) => {
        if (
          err.response?.status === 401 &&
          window.location.pathname !== '/login'
        ) {
          const token = networkRequest.headers?.Authorization;
          const tokenData = localStorageService.getDataFromToken(token);
          await this.axios
            .request({
              url: '/users/api/system-logs/error',
              method: RequestType.POST,
              headers: networkRequest.headers,
              data: {
                category: 'Authentication',
                flow: 'Resident unauthorized',
                message: `401 error.${JSON.stringify({
                  url: networkRequest.url,
                  method: networkRequest.method,
                  userEmail: tokenData?.email,
                  tokenExp: (tokenData?.exp || 0) * 1000,
                  dateNow: Date.now(),
                  isValid: localStorageService.isTokenValid(token),
                  isExp: localStorageService.isTokenExpired(token),
                })}`,
                token,
              },
            })
            .then(() => {
              console.log('Front unauthorized log sent');
            })
            .catch((error) => {
              console.error(
                'Failed to send unauthorized log',
                JSON.stringify(error)
              );
            });
          localStorageService.removeAllTokens();
          if (!window.location.pathname.includes('/qr-code')) {
            window.location.replace('/login');
          }
        }
        if (err.response?.status === 403) {
          if (
            err?.response?.data?.error?.message === 'Terms acceptance required'
          ) {
            window.location.href = '/confirm-terms';
          }
          if (
            err?.response?.data?.error?.message ===
            'Phone verification required'
          ) {
            window.location.href = '/confirm-phone';
          }
        }
        throw err;
      });
  }

  private async setAuthorization<T>(
    networkRequest: HttpRequest<T>,
    noAccess?: boolean
  ) {
    const accessToken = !noAccess && (await localStorageService.getJWTToken());
    networkRequest.headers = {
      Authorization: accessToken && `Bearer ${accessToken}`,
      ...(networkRequest.headers || {}),
    };

    if (!accessToken) delete networkRequest.headers.Authorization;
  }

  private async setLanguage<T>(
    networkRequest: HttpRequest<T>,
    addLanguage?: boolean
  ) {
    const langCode = addLanguage && (await localStorageService.getLangToken());

    networkRequest.params = {
      lang: langCode,
      ...(networkRequest.params || {}),
    };

    if (!langCode) delete networkRequest.params.lang;
  }

  private static transformAxiosResponseToHttpResponse<K>(
    response: AxiosResponse,
    Model: any
  ): HttpResponse<K> {
    return {
      headers: response.headers,
      status: response.status,
      data: ApiCommunicationManagerImpl.plainToClass<K>(Model, response.data),
    };
  }

  private static plainToClass<K>(objectType: { new (): K }, obj: any): K {
    return plainToClass(objectType, obj);
  }
}
